import React, { useRef, FC } from 'react';
import { useStoreState } from 'easy-peasy';
import classNames from 'classnames';
import { useToggle, useClickAway } from 'react-use';
import { Loading } from 'Components/nui';
import { FormattedMessage } from 'react-intl';
import { useProductById } from '~/hooks/useProducts';
import { useTradablePresets, useDeletePreset } from '~/hooks/useOrderPresets';

interface PresetItem {
  id: string;
  name: string;
  product: {
    id: string;
  };
}

interface MenuItemProps {
  item: PresetItem;
  setProduct: (product: any) => void;
  setPreset: (id: string) => void;
  toggleVisible: () => void;
}

const MenuItem: FC<MenuItemProps> = ({
  item,
  setProduct,
  setPreset,
  toggleVisible,
}) => {
  const deletePreset = useDeletePreset();
  const product = useProductById(item.product.id);

  const onClick = async () => {
    toggleVisible();
    setProduct(product);
    setPreset(item.id);
  };

  const onDelete = async () => {
    deletePreset.mutate(item.id);
  };

  return (
    <li>
      <button onClick={onClick}>{item.name}</button>
      {item.id && (
        <button
          className="icon-trash"
          title="Delete this preset"
          onClick={onDelete}
        />
      )}
    </li>
  );
};

interface PresetDropdownProps {
  orderType: 'bid' | 'offer';
  setProduct: (product: any) => void;
  setPreset: (id: string) => void;
}

export const PresetDropdown: FC<PresetDropdownProps> = (props) => {
  const solution = useStoreState((state) => state.auth.solution);
  const { data: filteredPresets = [], isLoading } = useTradablePresets(
    props.orderType,
    solution?.id
  );

  const ref = useRef(null);
  const [visible, toggleVisible] = useToggle(false);
  useClickAway(ref, () => {
    toggleVisible(false);
  });

  return (
    <div
      ref={ref}
      className={classNames('presets', {
        open: visible,
      })}
    >
      <button className="presets-dd" onClick={toggleVisible}>
        <FormattedMessage
          id="orderform-field-load-preset-label"
          description="label for 'Load a preset' field on new order form"
          defaultMessage="Load a preset"
        />
      </button>
      <nav className="presets-ul">
        <ul>
          {filteredPresets.length ? (
            filteredPresets.map((item) => (
              <MenuItem
                key={item.id}
                item={item}
                toggleVisible={toggleVisible}
                {...props}
              />
            ))
          ) : (
            <li>
              {isLoading ? (
                <Loading size="medium" />
              ) : (
                <FormattedMessage
                  id="orderform-field-load-preset-empty-helptext"
                  description="no presets helptext"
                  defaultMessage="No presets configured"
                />
              )}
            </li>
          )}
        </ul>
      </nav>
    </div>
  );
};
